import type { FuzzyValue } from './fuzzyNumbers';
import { divideFuzzy, fractionToFuzzy, integerToFuzzy } from './fuzzyMath';
import type { Fraction } from './fractionType';
import { frac, parseFraction } from './integerMath';


export type Framerate = Fraction;

/**
 * Given a string of the form "50/1", returns an object
 * representing the framerate or undefined if we cannot parse it.
 */
export function parseFramerate(fps?: string): Framerate | undefined {
  if (!fps) {
    return undefined;
  }

  try {
    return parseFraction(fps);
  } catch (ex) {
    return undefined;
  }
}


export function framesToSeconds(frames: number, framerate: Framerate): number {
  return (frames / framerate.numerator) * framerate.denominator;
}


export function secondsToFrames(seconds: number, framerate: Framerate): number {
  return Math.round((seconds * framerate.numerator) / framerate.denominator);
}

export function getFrameDurationFromFrameRateApproximation(mediaFrameRate: number): FuzzyValue {
  if (Number.isInteger(mediaFrameRate)) {
    return fractionToFuzzy(frac(1, mediaFrameRate));
  }

  if (mediaFrameRate == 29.97) {
    /*
      According to statistical sampling around 1% of media files stored in Mimir
      has a 30000/1001 frame rate. The frame rate 2997/100 is so uncommon that
      it did not show up in any of the 10000 media files sampled.
    */
    return { approximate: 1001 / 30000, accuracyWarning: 'Interpreted 29.97 as 30000/1001 FPS', precise: { numerator: 1001, denominator: 30000 } };
  }

  if (mediaFrameRate == 59.94) {
    /*
      According to statistical sampling around 0.2% of media files stored in
      Mimir has a 60000/1001 frame rate. The frame rate 5994/100 is so uncommon
      that it did not show up in any of the 10000 media files sampled.
    */
    return { approximate: 1001 / 60000, accuracyWarning: 'Interpreted 59.94 as 60000/1001 FPS', precise: { numerator: 1001, denominator: 60000 } };
  }

  return { approximate: 1 / mediaFrameRate };
}

export function tryGetPreciseFrameRateFromApproximation(approximateFrameRate: number) {
  const frameDuration = getFrameDurationFromFrameRateApproximation(approximateFrameRate);
  const frameRate = divideFuzzy(integerToFuzzy(1), frameDuration);
  if (!frameRate.precise) return null;
  return frameRate.precise;
}
